import { Link } from "react-router-dom";
import {
    ArrowRight,
    SearchX,
    WifiOff,
    RefreshCw,
    Plane,
    BedDouble,
    ShoppingCart,
    Cpu,
    Shirt,
    UtensilsCrossed,
    Tag,
    Globe,
    Sparkles,
    Percent,
    Gift,
    Gamepad2,
    HeartPulse,
    Home,
    Baby,
    Dumbbell,
    BookOpen,
    Wrench,
} from "lucide-react";

const CATEGORY_ICONS = {
    airplane: Plane,
    "airplane-outline": Plane,
    bed: BedDouble,
    cart: ShoppingCart,
    "hardware-chip": Cpu,
    shirt: Shirt,
    "fast-food": UtensilsCrossed,
    globe: Globe,
    sparkles: Sparkles,
    pricetag: Tag,
    percent: Percent,
    gift: Gift,
    gamepad: Gamepad2,
    "game-controller": Gamepad2,
    heart: HeartPulse,
    fitness: Dumbbell,
    home: Home,
    baby: Baby,
    book: BookOpen,
    construct: Wrench,
};

export const categoryIcon = (icon) => CATEGORY_ICONS[icon] || Tag;

export const inr = (v) =>
    v == null ? null : `₹${Number(v).toLocaleString("en-IN")}`;

export const money = (m) => {
    if (m == null) return null;
    if (typeof m === "object") return m.display ?? (m.amount != null ? inr(m.amount) : null);
    return inr(m);
};

export const SectionHeader = ({ eyebrow, title, linkTo, linkLabel = "View all", dark = false, testId }) => (
    <div className="flex flex-wrap items-end justify-between gap-4" data-testid={testId}>
        <div>
            {eyebrow && (
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-saffron">{eyebrow}</p>
            )}
            <h2 className={`mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl ${dark ? "text-cream" : "text-ink"}`}>
                {title}
            </h2>
        </div>
        {linkTo && (
            <Link
                to={linkTo}
                data-testid={testId ? `${testId}-view-all` : undefined}
                className={`group inline-flex items-center gap-2 text-sm font-medium transition-colors ${
                    dark ? "text-cream/80 hover:text-saffron" : "text-india hover:text-saffron"
                }`}
            >
                {linkLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
        )}
    </div>
);

export const CardSkeleton = ({ className = "" }) => (
    <div className={`animate-pulse rounded-2xl border border-ink/5 bg-white p-5 shadow-sm ${className}`}>
        <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-ink/5" />
            <div className="h-3 w-24 rounded bg-ink/5" />
        </div>
        <div className="mt-4 h-4 w-full rounded bg-ink/5" />
        <div className="mt-2 h-4 w-2/3 rounded bg-ink/5" />
        <div className="mt-5 h-6 w-20 rounded-full bg-ink/5" />
    </div>
);

export const OfferRowSkeleton = () => (
    <div className="mt-8 flex gap-5 overflow-hidden">
        {[0, 1, 2, 3].map((i) => (
            <CardSkeleton key={i} className="w-[270px] shrink-0" />
        ))}
    </div>
);

export const GridSkeleton = ({ count = 8 }) => (
    <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: count }).map((_, i) => (
            <CardSkeleton key={i} />
        ))}
    </div>
);

export const EmptyState = ({ title = "Nothing here yet", message, children }) => (
    <div
        data-testid="empty-state"
        className="mt-10 flex flex-col items-center rounded-3xl border border-dashed border-ink/15 bg-white/70 px-8 py-16 text-center"
    >
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-saffron-soft">
            <SearchX className="h-6 w-6 text-saffron" />
        </span>
        <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-ink">{title}</h3>
        {message && <p className="mt-2 max-w-md text-sm leading-relaxed text-smoke">{message}</p>}
        {children}
    </div>
);

export const ErrorState = ({ onRetry, message = "Couldn't reach SahiKhoj servers. Please try again." }) => (
    <div
        data-testid="error-state"
        className="mt-10 flex flex-col items-center rounded-3xl border border-dashed border-ink/15 bg-white/70 px-8 py-16 text-center"
    >
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ink/5">
            <WifiOff className="h-6 w-6 text-smoke" />
        </span>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-smoke">{message}</p>
        {onRetry && (
            <button
                type="button"
                onClick={onRetry}
                data-testid="retry-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition-colors hover:bg-india"
            >
                <RefreshCw className="h-4 w-4" />
                Try again
            </button>
        )}
    </div>
);
